import React, { useEffect, useRef, useState } from 'react';
import Inputmask from 'inputmask';
import { Drawer } from 'vaul';
import { Loader, Save, X } from 'lucide-react';
import Spacing from '../Spacing';
import Skeleton from 'react-loading-skeleton';
import { fetchNpiData } from '../../services/workerServices';
import { notify } from '../../helpers';

export default function IPAImportViewModal(props) {
  const {
    openModal = false,
    setOpenModal = () => {},
    previousScrollPosition = 0,
    enableLoader = false,
    modalAction = () => {},
    data = [],
    account_id = null,
    location_id = null,
    setAllIpaData = () => {},
    setIpaFile = () => {},
    wsActive = false,
    wsDone = false,
    wsLogs = []
  } = props;

  const dropdownRef = useRef(null);
  const wsLogEndRef = useRef(null);
  const [isSaved, setIsSaved] = useState(false);
  const [existingList, setExistingList] = useState([]);
  const [readyList, setReadyList] = useState([]);
  const [moreInfoList, setMoreInfoList] = useState([]);
  const [showExisting, setShowExisting] = useState(false);
  const [showReady, setShowReady] = useState(false);
  const [showMoreInfo, setShowMoreInfo] = useState(true);
  const [npiLoadingIndex, setNpiLoadingIndex] = useState(null);
  const [notifyEmail, setNotifyEmail] = useState(null);
  const [rowMoreInfoList, setRowMoreInfoList] = useState([]);

  const safeJsonParse = (s) => {
    try {
      if (typeof s !== 'string') return null;
      const t = s.trim();
      if (!t || t[0] !== '{') return null;
      return JSON.parse(t);
    } catch {
      return null;
    }
  };

  const wsEvents = (Array.isArray(wsLogs) ? wsLogs : [])
    .map((line) => {
      const raw = String(line);
      const obj = safeJsonParse(raw);
      return { raw, obj };
    });

  // Keep only JSON events and show only the latest 100 to avoid heavy UI on huge imports
  const wsJsonEvents = wsEvents.filter(e => !!e.obj);
  const wsJsonEventsLatest = wsJsonEvents.slice(-100);

  const lastProgressEvent = [...wsEvents]
    .reverse()
    .find((e) => e?.obj && (e.obj.progress !== undefined || e.obj.index !== undefined || e.obj.total !== undefined));

  const progressValue = (() => {
    const p = lastProgressEvent?.obj?.progress;
    if (typeof p === 'number' && isFinite(p)) {
      return p;
    }
    const idx = lastProgressEvent?.obj?.index;
    const total = lastProgressEvent?.obj?.total;
    if (typeof idx === 'number' && typeof total === 'number' && total > 0) {
      return (idx / total) * 100;
    }
    return 0;
  })();

  const progressBarPct = Math.max(0, Math.min(100, progressValue));

  // Auto-scroll WS logs
  useEffect(() => {
    if (!(wsActive || wsDone)) return;
    wsLogEndRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
  }, [wsLogs, wsActive, wsDone]);

  const mapItem = (item) => {
    return {
      npi_type: item?.npi_type || '',
      number: item?.number || '',
      practice_name: item?.practice_name || '',
      first_name: item?.first_name || '',
      last_name: item?.last_name || '',
      mobile_phone: item?.mobile_phone || '',
      gender: item?.gender || '',
      address: item?.address || '',
      city: item?.city || '',
      state: item?.state || '',
      postal_code: item?.postal_code || '',
      practice_phone: item?.practice_phone || '',
      fax_number: item?.fax_number || '',
    };
  };

  // Initialize / sync lists from data
  useEffect(() => {
    const existing = Array.isArray(data?.existing_practice_list) ? data.existing_practice_list.map(mapItem) : [];
    const ready = Array.isArray(data?.ready_to_onboard_list) ? data.ready_to_onboard_list.map(mapItem) : [];
    const moreInfoRaw = Array.isArray(data?.more_info_needed_list) ? data.more_info_needed_list.map(mapItem) : [];
    const moreInfo = moreInfoRaw.map((item) => ({ ...item, old_practice_name: item?.practice_name || '' }));

    setExistingList(existing);
    setReadyList(ready);
    setMoreInfoList(moreInfo);
    setRowMoreInfoList(moreInfo);
    setIsSaved(false);
  }, [data]);

  // Prevent dismissal until saved (and while WS is running)
  const guardClose = (intent) => {
    if (!isSaved || (wsActive && !wsDone)) {
      intent?.preventDefault?.();
      return false;
    }
    return true;
  };

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen) {
      if (!guardClose()) return;
      setOpenModal(false);
      window.scrollTo(0, previousScrollPosition || 0);
    } else {
      setOpenModal(true);
    }
  };

  // Disable ESC close while unsaved / while WS is active
  useEffect(() => {
    const onKey = (e) => {
      if (!openModal) return;
      if ((e.key === 'Escape' || e.key === 'Esc') && (!isSaved || (wsActive && !wsDone))) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [openModal, isSaved, wsActive, wsDone]);

  useEffect(() => {
    if (!openModal) return;
    const phoneInputs = document.querySelectorAll('.us_phone');
    if (phoneInputs.length > 0) {
      Inputmask({
        mask: '(999) 999-9999',
        showMaskOnHover: false,
        showMaskOnFocus: true,
      }).mask(phoneInputs);
    }
  }, [openModal, moreInfoList.length, existingList.length, readyList.length]);

  const handleMoreInfoChange = (e, index) => {
    const { name, value } = e.target;
    let newValue = value;
    if (name === 'number') {
      newValue = value.replace(/\D/g, '');
    }

    setMoreInfoList((prev) =>
      prev.map((item, i) =>
        i === index ? {
          ...item,
          [name]: newValue,
        } : item
      )
    );

    if (name === 'number') {
      const cleanedNpi = newValue;
      if (cleanedNpi.length === 10) {
        lookupNpiAndFillRow(cleanedNpi, index);
      }
    }
  };

  // NPI lookup → fetch NPI details and auto-fill row
  const lookupNpiAndFillRow = async (npi, index) => {
    if (!npi || npi.length !== 10) {
      return;
    }
    let prevItem = rowMoreInfoList[index];

    try {
      setNpiLoadingIndex(index);
      const payload = {
        account_id: account_id,
        location_id: location_id,
        npi: npi
      }
      const response = await fetchNpiData(payload);

      if (!response) {
        if (prevItem) {
          setMoreInfoList((prev) =>
            prev.map((item, i) => (i === index ? { ...prevItem } : item))
          );
        }
        return;
      }

      if (response?.detail) {
        notify('error', response?.detail);

        if (prevItem) {
          setMoreInfoList((prev) =>
            prev.map((item, i) => (i === index ? { ...prevItem } : item))
          );
        }
        return;
      }

      if (response?.data?.status !== 'success') {
        notify('error', response?.data?.message || 'NPI not found');

        if (prevItem) {
          setMoreInfoList((prev) =>
            prev.map((item, i) => (i === index ? { ...prevItem } : item))
          );
        }
        return;
      }

      const d = response?.data?.data;
      setMoreInfoList((prev) =>
        prev.map((item, i) =>
          i === index ? {
            ...item,
            npi_type: d?.npi_type || item?.npi_type,
            number: d?.number || npi,
            practice_name: d?.practice_name || item?.practice_name,
            first_name: d?.first_name || item?.first_name,
            last_name: d?.last_name || item?.last_name,
            mobile_phone: d?.mobile_phone || item?.mobile_phone,
            gender: d?.gender || item?.gender,
            address: d?.address || item?.address,
            city: d?.city || item?.city,
            state: d?.state || item?.state,
            postal_code: d?.postal_code || item?.postal_code,
            practice_phone: d?.practice_phone || item?.practice_phone,
            fax_number: d?.fax_number || item?.fax_number,
          } : item
        )
      );
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 5);
      }

      if (prevItem) {
        setMoreInfoList((prev) =>
          prev.map((item, i) => (i === index ? { ...prevItem } : item))
        );
      }
    } finally {
      setNpiLoadingIndex(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      account_id: account_id,
      location_id: location_id,
      notification_email: notifyEmail,
      existing_practice_list: existingList,
      ready_to_onboard_list: readyList,
      more_info_needed_list: moreInfoList,
    };

    modalAction(payload);
    setIsSaved(true);
  };

  const hasAnyData =
    (Array.isArray(data?.existing_practice_list) && data?.existing_practice_list?.length > 0) ||
    (Array.isArray(data?.ready_to_onboard_list) && data?.ready_to_onboard_list?.length > 0) ||
    (Array.isArray(data?.more_info_needed_list) && data?.more_info_needed_list?.length > 0);

  const isLoading = !hasAnyData;

  return (
    <>
      <div style={{ height: '100%' }} ref={dropdownRef}>
        <Drawer.Root open={openModal} onOpenChange={handleOpenChange} dismissible={false}>
          <Drawer.Portal>
            <Drawer.Overlay className="docpro_drawer_overlay" />
            <Drawer.Content className="docpro_drawer_content docpro_drawer_style_4" onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
              <Drawer.Title className="sr-only">IPA Details</Drawer.Title>
              <Drawer.Description className="sr-only">IPA details of a file</Drawer.Description>

              <div className="docpro_modal_wrap docpro_style_1 docpro_modal_lg">
                <div className="docpro_modal" style={{ backgroundColor: '#F9F9FB' }}>

                  {(wsActive || (Array.isArray(wsLogs) && wsLogs.length > 0)) ? (
                    <div className="docpro_border_heading_card w-full docpro_white_bg" style={{ border: '1px solid var(--vivid)' }}>
                      <div className="card_heading flex items-center docpro_vivid_bg" style={{ justifyContent: 'space-between', borderRadius: '6px' }}>
                        <h1 className="mb-0 docpro_fs_18 docpro_white_color">
                          IPA Update Progress{wsDone ? ' (Completed)' : ''}
                        </h1>
                        <span className="docpro_text_uppercase docpro_fs_12 docpro_white_color" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {wsActive ? (
                            <>
                              <span className="docpro_live_blink_dot" aria-hidden="true" />
                              <span style={{ marginBottom: '-2px' }}>On Progress</span>
                            </>
                          ) : 'Completed'}
                        </span>
                        {wsDone && (
                          <button
                            type="button"
                            onClick={() => {
                              setAllIpaData([]);
                              setIpaFile(null);
                              setOpenModal(false);
                              window.scrollTo(0, previousScrollPosition || 0);
                            }}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: 'var(--red)',
                              padding: '6px 10px',
                              fontWeight: 700,
                              cursor: 'pointer',
                              position: 'absolute',
                              top: 0,
                              right: 0,
                            }}
                            aria-label="Close progress"
                          >
                            <X width={22} height={22} />
                          </button>
                        )}
                      </div>

                      <div className="card_body" style={{ background: '#fff', borderRadius: 6 }}>
                        <div style={{ marginBottom: 10 }}>
                          <div className="flex items-center" style={{ justifyContent: 'space-between', gap: 10 }}>
                            <div style={{ color: '#111827', fontWeight: 600 }}>
                              {lastProgressEvent?.obj?.total ? `Total: ${lastProgressEvent.obj.total}` : ''}
                            </div>
                            <div style={{ color: '#111827', fontWeight: 700 }}>
                              {progressValue ? `${Number(progressValue).toFixed(2).replace(/\.00$/, '')}%` : ''}
                            </div>
                          </div>
                          <div style={{ height: 8, borderRadius: 999, background: 'rgba(15, 23, 42, 0.10)', overflow: 'hidden', marginTop: 6 }}>
                            <div style={{ height: '100%', width: `${progressBarPct}%`, background: 'rgba(11, 104, 203, 0.85)' }} />
                          </div>
                          {lastProgressEvent?.obj?.details && (
                            <div style={{ marginTop: 8, color: '#374151' }}>
                              <strong>Latest:</strong> {String(lastProgressEvent.obj.details)}
                            </div>
                          )}
                        </div>


                        <div
                          className="custom-scroll"
                          style={{
                            borderRadius: 8,
                            border: '1px solid var(--border)',
                            background: '#0b1220',
                            color: '#e6edf3',
                            padding: 12,
                            maxHeight: 'calc(100vh - 282px)',
                            overflow: 'auto',
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                            fontSize: 12,
                            lineHeight: 1.5
                          }}
                        >
                          {wsJsonEventsLatest.map((e, i) => {
                            const o = e.obj || {};
                            const status = o.status ?? '';
                            const idx = (o.index !== undefined && o.index !== null) ? String(o.index) : '';
                            const total = (o.total !== undefined && o.total !== null) ? String(o.total) : '';
                            const details = o.details ?? '';
                            return (
                              <div
                                key={i}
                                style={{
                                  display: 'flex',
                                  gap: 10,
                                  padding: '3px 0',
                                  borderBottom: '1px solid rgba(255,255,255,0.06)'
                                }}
                              >
                                <span style={{ minWidth: 78, opacity: 0.95 }}>{status}</span>
                                <span style={{ minWidth: 96, opacity: 0.9 }}>
                                  {idx && total ? `${idx}/${total}` : total ? `0/${total}` : ''}
                                </span>
                                <span style={{ flex: 1, opacity: 0.95 }}>{details}</span>
                              </div>
                            );
                          })}
                          <div ref={wsLogEndRef} />
                        </div>

                        <Spacing lg="12" sm="12" />
                        <div className="flex items-center" style={{ justifyContent: 'space-between' }}>
                          <span style={{ color: 'var(--ternary)' }}>
                            {wsActive ? 'Waiting for updates…' : wsDone ? 'Update finished.' : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    isLoading ? (
                      <>
                        {Array.from({ length: 3 }).map((_, index) => (
                          <div
                            key={index}
                            className="docpro_border_heading_card w-full"
                            style={{ marginBottom: '16px' }}
                          >
                            <div className="card_heading">
                              <Skeleton width={`${20 + (index % 2)}rem`} borderRadius="8px"></Skeleton>
                            </div>
                            <div className="card_body">
                              <div className="docpro_grid docpro_grid_gap_16 docpro_grid_col_3 w-full rounded-md">
                                <div className="docpro_grid_item">
                                  <Skeleton width={`${8 + (index % 5)}rem`} borderRadius="8px"></Skeleton>
                                </div>
                                <div className="docpro_grid_item">
                                  <Skeleton width={`${10 + (index % 3)}rem`} borderRadius="8px"></Skeleton>
                                </div>
                                <div className="docpro_grid_item">
                                  <Skeleton width={`${8 + (index % 2)}rem`} borderRadius="8px"></Skeleton>
                                </div>
                                <div className="docpro_grid_item">
                                  <Skeleton width={`${8 + (index % 4)}rem`} borderRadius="8px"></Skeleton>
                                </div>
                                <div className="docpro_grid_item">
                                  <Skeleton width={`${10 + (index % 2.5)}rem`} borderRadius="8px"></Skeleton>
                                </div>
                                <div className="docpro_grid_item">
                                  <Skeleton width={`${8 + (index % 3)}rem`} borderRadius="8px"></Skeleton>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </>
                    ) : (
                      <form onSubmit={handleSubmit} className='docpro_ipa_import_form'>
                        <div>
                          <label className="docpro_fw_500 docpro_primary_color">
                            Email Address <span className="docpro_red_color">*</span>
                          </label>
                          <input
                            type="email"
                            name="email"
                            className="docpro_form_field size_lg"
                            onChange={(e) => {setNotifyEmail(e.target.value);}}
                            placeholder="Enter email address"
                            required
                          />
                        </div>
                        <Spacing lg="16" sm="16" />

                        {/* EXISTING PRACTICES (collapsible table) */}
                        {existingList.length > 0 && (
                          <>
                            <div
                              className="docpro_border_heading_card w-full docpro_white_bg"
                              style={{ border: '1px solid var(--vivid)' }}
                            >
                              <div
                                className="card_heading flex items-center pointer docpro_vivid_bg"
                                style={{ justifyContent: 'space-between', borderRadius: showExisting ? '6px 6px 0 0' : '6px', borderBottom: !showExisting ? '0' : '' }}
                                onClick={() => setShowExisting((v) => !v)}
                              >
                                <h1 className="mb-0 docpro_fs_18 docpro_white_color">
                                  Existing Practice List ({existingList.length})
                                </h1>
                                <span className='docpro_text_uppercase docpro_fs_12 docpro_white_color'>
                                  {showExisting ? 'Hide' : 'Show'}
                                </span>
                              </div>
                              {showExisting && (
                                <div className="card_body">
                                  <div className="docpro_data_table_wrap" style={{ borderRadius: 8 }}>
                                    <div className="docpro_table_responsive custom-scroll" style={{ borderRadius: 8 }}>
                                      <table className="docpro_mb_0">
                                        <thead>
                                          <tr>
                                            <th style={{ borderTop: 0 }}>NPI</th>
                                            <th style={{ borderTop: 0 }}>Practice Name</th>
                                            <th style={{ borderTop: 0 }}>City</th>
                                            <th style={{ borderTop: 0 }}>State</th>
                                            <th style={{ borderTop: 0 }}>Zip</th>
                                            <th style={{ borderTop: 0 }}>Name</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {existingList.map((item, index) => (
                                            <tr key={index}>
                                              <td><kbd className="docpro_kbd">{item?.number}</kbd></td>
                                              <td>{item?.practice_name}</td>
                                              <td>{item?.city}</td>
                                              <td>{item?.state}</td>
                                              <td>{item?.postal_code}</td>
                                              <td>{item?.last_name} {item?.first_name}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                            <Spacing lg="16" sm="16" />
                          </>
                        )}

                        {/* READY TO ONBOARD (collapsible table) */}
                        {readyList.length > 0 && (
                          <>
                            <div
                              className="docpro_border_heading_card w-full docpro_white_bg"
                              style={{ border: '1px solid var(--green)' }}
                            >
                              <div
                                className="card_heading flex items-center pointer docpro_green_bg"
                                style={{ justifyContent: 'space-between', borderRadius: showReady ? '6px 6px 0 0' : '6px', borderBottom: !showReady ? '0' : '' }}
                                onClick={() => setShowReady((v) => !v)}
                              >
                                <h1 className="mb-0 docpro_fs_18 docpro_white_color">
                                  Ready To Onboard List ({readyList.length})
                                </h1>
                                <span className='docpro_text_uppercase docpro_fs_12 docpro_white_color'>
                                  {showReady ? 'Hide' : 'Show'}
                                </span>
                              </div>
                              {showReady && (
                                <div className="card_body">
                                  <div className="docpro_data_table_wrap" style={{ borderRadius: 8 }}>
                                    <div className="docpro_table_responsive custom-scroll" style={{ borderRadius: 8 }}>
                                      <table className="docpro_mb_0">
                                        <thead>
                                          <tr>
                                            <th style={{ borderTop: 0 }}>NPI</th>
                                            <th style={{ borderTop: 0 }}>Practice Name</th>
                                            <th style={{ borderTop: 0 }}>City</th>
                                            <th style={{ borderTop: 0 }}>State</th>
                                            <th style={{ borderTop: 0 }}>Zip</th>
                                            <th style={{ borderTop: 0 }}>Name</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {readyList.map((item, index) => (
                                            <tr key={index}>
                                              <td><kbd className="docpro_kbd">{item?.number}</kbd></td>
                                              <td>{item?.practice_name}</td>
                                              <td>{item?.city}</td>
                                              <td>{item?.state}</td>
                                              <td>{item?.postal_code}</td>
                                              <td>{item?.last_name} {item?.first_name}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                            <Spacing lg="16" sm="16" />
                          </>
                        )}

                        {/* MORE INFO NEEDED (collapsible table) */}
                        {moreInfoList.length > 0 && (
                          <>
                            <div
                              className="docpro_border_heading_card w-full docpro_white_bg"
                              style={{ border: '1px solid var(--warning)' }}
                            >
                              <div
                                className="card_heading flex items-center pointer docpro_warning_bg"
                                style={{ justifyContent: 'space-between', borderRadius: showMoreInfo ? '6px 6px 0 0' : '6px', borderBottom: !showMoreInfo ? '0' : '' }}
                                onClick={() => setShowMoreInfo((v) => !v)}
                              >
                                <h1 className="mb-0 docpro_fs_18 docpro_white_color">
                                  More Info Needed List ({moreInfoList.length})
                                </h1>
                                <span className='docpro_text_uppercase docpro_fs_12 docpro_white_color'>
                                  {showMoreInfo ? 'Hide' : 'Show'}
                                </span>
                              </div>
                              {showMoreInfo && (
                                <div className="card_body">
                                  <div className="docpro_data_table_wrap" style={{ borderRadius: 8 }}>
                                    <div className="docpro_table_responsive custom-scroll" style={{ borderRadius: 8 }}>
                                      <table className="docpro_mb_0">
                                        <thead>
                                          <tr>
                                            <th style={{ borderTop: 0 }}>NPI</th>
                                            <th style={{ borderTop: 0 }}>Practice Name</th>
                                            <th style={{ borderTop: 0 }}>City</th>
                                            <th style={{ borderTop: 0 }}>State</th>
                                            <th style={{ borderTop: 0 }}>Zip</th>
                                            <th style={{ borderTop: 0 }}>Name</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {moreInfoList.map((item, index) => (
                                            <tr key={index} className='relative'>
                                              <td>
                                                <input
                                                  type="text"
                                                  name="number"
                                                  className="docpro_form_field docpro_no_border_input"
                                                  value={item?.number}
                                                  minLength={10}
                                                  maxLength={10}
                                                  onChange={(e) => handleMoreInfoChange(e, index)}
                                                  placeholder='Enter NPI'
                                                />
                                              </td>
                                              <td>
                                                <span style={{ color: item?.state ? 'var(--secondary)' : 'var(--ternary)' }}>{item?.practice_name}</span>
                                              </td>
                                              <td>{item?.city}</td>
                                              <td>{item?.state}</td>
                                              <td>{item?.postal_code}</td>
                                              <td>{item?.last_name} {item?.first_name}</td>
                                              {npiLoadingIndex === index && (
                                                <td colSpan={6} className="docpro_loading" style={{ backgroundColor: 'rgb(218 218 218 / 61%)', width: '100%', height: '100%' }}>
                                                  <Loader width={25} height={25} className="docpro_accent_color" />
                                                </td>
                                              )}
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                            <Spacing lg="16" sm="16" />
                          </>
                        )}

                        <Spacing lg="40" sm="40" />
                        <div className="text-right docpro_modal_fixed_btn">
                          <button
                            type="submit"
                            style={{ borderRadius: '4px' }}
                            className="docpro_bordered_btn size_lg docpro_fw_600 docpro_white_color docpro_filed_btn"
                            disabled={enableLoader}
                          >
                            <Save width={17} height={17} />
                            <span>Save</span>
                            {enableLoader && (
                              <span className="docpro_loading">
                                <Loader width={20} height={20} />
                              </span>
                            )}
                          </button>
                        </div>
                      </form>
                    )
                  )}

                </div>
              </div>
            </Drawer.Content>
          </Drawer.Portal>
        </Drawer.Root>
      </div>
    </>
  );
}
